/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";

// XNG-Change
const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const path =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const searchPath = getSettingsCollection(state).searchSettings.searchPath;
    return {
        currentPage: getCurrentPage(state),
        isBrandPage: !!catalogPage?.brandId,
        isSearchResultsPage: location.pathname.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`),
    };
};

interface Props extends WidgetProps, HasCategoryContext, ReturnType<typeof mapStateToProps> {}

export interface CategoryDetailBannerStyles {
    container?: InjectableCss;
    // XNG-Change
    skeletonContainer?: InjectableCss;
}

export const bannerStyles: CategoryDetailBannerStyles = {};

const styles = bannerStyles;

const CategoryDetailBanner = ({ category, isSearchResultsPage, isBrandPage }: Props) => {
    // XNG-Change
    if (!category) {
        if (isSearchResultsPage || isBrandPage) {
            return null;
        }

        return (
            <StyledWrapper {...styles.skeletonContainer}>
                <Skeleton height="200px" />
            </StyledWrapper>
        );
    }

    // XNG-Change
    if (!category.htmlContent) {
        return null;
    }

    return (
        // xng-change - using section for the wrapper to make it more semantic
        <StyledWrapper as="section" {...styles.container} data-test-selector="categoryDetailBanner_htmlContent">
            {parse(transformContentHtml(category.htmlContent), parserOptions)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryDetailBanner)),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
